import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import { Basket as NBasket } from '@df/basket/basket.interfaces';
import { IProductDataV2 } from '@df/catalog/product/product';
import { Status } from '@df/core/status';
import { AbModule } from '@df/module-ab/ab.module';
import { GtmService } from '@df/module-gtm/gtm.service';
import { OffClickDirective } from '@df/module-off-click/off-click.directive';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { WishlistItem } from '@df/session/api/wishlist/wishlist-item';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { Subscription } from 'rxjs/internal/Subscription';
import { ProductDecorator } from '../../catalog/model/product/product.decorator';
import { BasketLoadingStateComponent } from '../../catalog/ui/basket/basket-loading-state.component';
import { BasketOosItemsWarningComponent } from '../../catalog/ui/basket/basket-oos-items-warning.component';
import { InternalProductsService } from '../../catalog/ui/product/internal-products.service';
import { wishlistSort } from '../../catalog/ui/wishlist/wishlist-sort.pipe';
import { WishlistDecorator } from '../../catalog/ui/wishlist/wishlist.decorator';
import { MinibasketFastCheckoutFormComponent } from '../../checkout/fast/minibasket-fast-checkout-form.component';
import { BasketDecorator } from '../../customer/api/basket/basket.decorator';
import { NLoginFormModal } from '../../login/form/modal/login-form-modal.interface';
import { MinibasketComponentDef } from '../deprecated/minibasket.component.def';
import { BasketWishlistItemComponent } from '../wishlist/basket-wishlist-item.component';

@Component({
    selector: 'minibasket',
    templateUrl: './minibasket.component.html',
    styleUrl: './minibasket.component.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        AbModule,
        BasketLoadingStateComponent,
        BasketOosItemsWarningComponent,
        BasketWishlistItemComponent,
        CommonModule,
        MinibasketFastCheckoutFormComponent,
        OffClickDirective,
        SizeClassDirective,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MinibasketComponent extends MinibasketComponentDef implements OnInit, OnDestroy {
    @ViewChild('minibasketScrollContainer', { static: false })
    scrollContainer!: ElementRef;

    @ViewChildren(BasketWishlistItemComponent)
    basketWishlistItems!: QueryList<BasketWishlistItemComponent>;

    wishlistItems: WishlistItem[] = [];

    removeStatus = new Status();
    status = new Status();

    displayedDataSubscription!: Subscription;
    showAddToBasketMessage = false;
    showMoveToWishlistMessage = false;
    currentTab: 'basket' | 'wishlist' = 'basket';

    protected gtmService = inject(GtmService);
    private dfModalService = inject(DfModalService);
    private internalProductsService = inject(InternalProductsService);
    protected readonly disposableTimeoutService = injectTimeoutService();

    override get basket(): BasketDecorator {
        return this.app.customer.basket as BasketDecorator;
    }

    get wishlist(): WishlistDecorator {
        return this.app.customer.wishlist as WishlistDecorator;
    }

    get displayFastCheckout(): boolean {
        return !!(this.wasEverVisible && this.basket.qty && this.canShow());
    }

    override ngOnInit() {
        this._subscriptions.add(this.wishlist.subscribe(() => this.updateWishlistItems()));

        this.displayedDataSubscription = this.basket.subscribeDisplayedData(() => this.detectChanges());

        this._subscriptions.add(this.removeStatus.subscribe(() => this.detectChanges()));

        super.ngOnInit();
    }

    override ngOnDestroy() {
        if (this.displayedDataSubscription) {
            this.displayedDataSubscription.unsubscribe();
        }

        super.ngOnDestroy();
    }

    override show(...args) {
        this.updateWishlistItems();

        if (args[0][0]?.data?.['wishlist']) {
            this.switchTab('wishlist');
        } else {
            this.switchTab('basket');
        }

        super.show(args);
    }

    showAddToBasketNotification() {
        this.showAddToBasketMessage = true;
        this.detectChanges();
        this.disposableTimeoutService.setTimeout(() => {
            this.showAddToBasketMessage = false;
            this.detectChanges();
        }, 3000);
    }

    showMoveToWishlistNotification(): void {
        this.showMoveToWishlistMessage = true;
        this.detectChanges();
        this.disposableTimeoutService.setTimeout(() => {
            this.showMoveToWishlistMessage = false;
            this.detectChanges();
        }, 3000);
    }

    scrollToBottom() {
        const element = this.scrollContainer?.nativeElement;
        if (element?.children?.[0]) {
            element.scroll({
                top: element.children[0].clientHeight,
                behavior: 'smooth'
            });
        }
    }

    switchTab(tab: 'basket' | 'wishlist') {
        if (this.currentTab === 'wishlist' && tab === 'basket') {
            this.gtmService.push({ event: 'closeWishlist' });
        }

        this.currentTab = tab;

        if (tab === 'wishlist') {
            this.gtmService.push({ event: 'openWishlist' });
        }

        this.detectChanges();
    }

    addAllToBasket() {
        if (this.basketWishlistItems.length > 0) {
            this.removeStatus.busy = true;

            this.basketWishlistItems.first.addToBasket(true).finally(() => {
                this.removeStatus.busy = false;
            });
        }
    }

    override hide(detectChanges = true) {
        if (this.isVisible) {
            this.gtmService.push({ event: 'closeMinibasket' });

            if (this.currentTab === 'wishlist') {
                this.gtmService.push({ event: 'closeWishlist' });
            }
        }

        this.currentTab = 'basket';
        super.hide(detectChanges);
    }

    protected openLoginModal() {
        this.hide();
        this.dfModalService.open(undefined, NLoginFormModal.LOGIN_FORM_MODAL_ID, undefined, undefined);
    }

    protected override subscribeToBasketAdd() {
        this.basketSuccessSubscription = this.basketService.subscribeToAddToBasketSuccess(() => {
            if (!this.client.isS && !this.client.isM) {
                super.show();

                this.runExperiment('minibasketCheckoutButton');
            }
        });
    }

    protected override onShow(): void {
        this.wasEverVisible = true;
        this.gtmService.push({ event: 'openMinibasket' });

        this.basket.preloadData().then(() => {
            this.app.events.broadcast(NBasket.EVENTS.view);
        });

        super.onShow();
    }

    protected updateWishlistItems() {
        const wishlistItemsIds = this.wishlist.items.map(item => item.productId);

        this.app.resources.product
            .loadAllIds(wishlistItemsIds, 2, { safe: true })
            .then(() => {
                this.wishlistItems = wishlistSort(
                    this.wishlist.items.filter(
                        i =>
                            i.product &&
                            this.internalProductsService.isProductAvailable(i.product) &&
                            !(<ProductDecorator<IProductDataV2>>i.product).isBanner
                    )
                );

                this.detectChanges();
            })
            .catch(() => {
                this.wishlistItems = [];

                this.detectChanges();
            });
    }
}
